/**
 * Reusable File Upload UI Component
 * Supports drag & drop, file type validation, and image preview
 */

import { useState, useRef, useCallback } from "react";
import { UploadImageIcon } from "@/assets/svg";

export type FileUploadVariant = "image" | "document";

export interface FileUploadProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (fileName: string, file?: File | null) => void;
  readonly accept?: string;
  readonly acceptText?: string;
  readonly maxSizeText?: string;
  readonly templateLink?: {
    label: string;
    onClick: () => void;
  };
  readonly errorMessage?: string;
  readonly variant?: FileUploadVariant;
  readonly heightClass?: string;
  readonly showIcon?: boolean;
  readonly validateFileType?: (file: File) => boolean;
}

export const FileUpload = ({
  id,
  value,
  onChange,
  accept = "*",
  acceptText = "",
  maxSizeText = "",
  templateLink,
  errorMessage,
  variant = "image",
  heightClass = "h-[200px]",
  showIcon = true,
  validateFileType,
}: FileUploadProps): React.JSX.Element => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if the value is a base64 data URL
  const isBase64DataUrl = value.startsWith("data:") && value.includes(";base64,");

  // Determine the effective preview URL (internal or base64 value)
  const effectivePreviewUrl = previewUrl ?? (isBase64DataUrl ? value : null);

  // Determine the display name for the file
  const displayFileName = fileName ?? (isBase64DataUrl ? "Image uploaded" : value);

  const handleFile = useCallback(
    (file: File) => {
      setInternalError(null);
      setFileName(file.name);

      // For image variant, create preview URL
      if (variant === "image" && file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }

      // Validate file type if validator provided
      if (validateFileType && !validateFileType(file)) {
        setInternalError("Invalid file type");
        return;
      }

      onChange(file.name, file);
    },
    [onChange, validateFileType, variant]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const displayError = errorMessage || internalError;
  const hasFile = Boolean(value);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={`
          relative flex flex-col items-center justify-center gap-2
          ${heightClass} rounded-lg border-2 border-dashed cursor-pointer
          transition-colors duration-200
          ${
            isDragging
              ? "border-[#001A41] bg-blue-50"
              : "border-gray-300 bg-white"
          }
          ${displayError ? "border-red-500" : ""}
          hover:border-[#001A41] hover:bg-gray-50
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          id={id}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
        />

        {variant === "image" && effectivePreviewUrl ? (
          <div className="w-full h-full flex flex-col p-2">
            <div className="flex-1 flex items-center justify-center min-h-0">
              <img
                src={effectivePreviewUrl}
                alt="Preview"
                className="max-w-full max-h-full object-contain rounded"
              />
            </div>
            <div className="text-center mt-2 flex-shrink-0">
              <span className="text-sm text-gray-700 font-medium">{displayFileName}</span>
            </div>
          </div>
        ) : hasFile && variant === "document" ? (
          <div className="flex flex-col items-center gap-2">
            {showIcon && <UploadImageIcon />}
            <span className="text-sm text-gray-700 font-medium">{displayFileName}</span>
            {(acceptText || maxSizeText || templateLink) && (
              <div className="text-xs text-gray-400 text-center">
                {acceptText && <div>{acceptText}</div>}
                {maxSizeText && !templateLink && <div>{maxSizeText}</div>}
              </div>
            )}
            {templateLink && (
              <div className="text-xs text-gray-400 text-center">
                File template:{" "}
                <a
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    templateLink.onClick();
                  }}
                  className="text-[#001A41] underline font-medium hover:text-blue-800 bg-transparent"
                >
                  {templateLink.label}
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {showIcon && <UploadImageIcon />}
            <div className="text-center">
              Drop your files here or{" "}
              <span className="text-blue-600 font-semibold">browse</span>
            </div>
            {(acceptText || maxSizeText || templateLink) && (
              <div className="text-xs text-gray-400 text-center">
                {acceptText && <div>{acceptText}</div>}
                {maxSizeText && !templateLink && <div>{maxSizeText}</div>}
                {templateLink && (
                  <div>
                    File template:{" "}
                    <a
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        templateLink.onClick();
                      }}
                      className="text-[#001A41] underline font-medium hover:text-blue-800 bg-transparent"
                    >
                      {templateLink.label}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {displayError && (
        <span className="text-xs text-red-600">{displayError}</span>
      )}
    </div>
  );
};
